import React, { useEffect, useState } from "react";
import { t, Trans } from "@lingui/macro";
import {
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useLingui } from "@lingui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRequest } from "@/utils/request";
import { signIn, useSession } from "next-auth/react";
import { useCheckoutStore } from "@/components/context/checkout-store";
import { useAlertStore } from "@/components/context/alert-store";
import { useRouter } from "next/navigation";
interface OtpFormProps {
  lang: string;
  handleChange: () => void;
}

const OtpForm: React.FC<OtpFormProps> = ({ handleChange, lang }) => {
  const { i18n } = useLingui();
  const [otp, setOtp] = useState<string>("");
  const [isResendOtpLoaderVisible, setIsResendOtpLoaderVisible] = useState<boolean>(false);
  const [resendOTP, setResendOTP] = useState<boolean>(true);
  const [isProgressDisplayed, setIsProgressDisplayed] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(20);
  const [email, setEmail] = useState<string | null>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { setMessage } = useAlertStore();
  const router = useRouter();
  const resendOtpMutation = useMutation({
    mutationFn: (data: FormData) =>
      postRequest({ path: { name: "resendOTP" }, formData: data, method: "POST", lang }),
    onSuccess: () => {
      localStorage.setItem("current-email", email || "");
      setResendOTP(false);
      setMessage({
        text: t(i18n)`We have sent a new 4 digit OTP on your Email Address.`,
        severity: "success",
      });
      setIsResendOtpLoaderVisible(false);
    },
    onError: () => {
      setIsResendOtpLoaderVisible(false);
    },
  });

  const { data: session, update } = useSession();
  const [isGuest, setIsGuest] = useState<boolean>(false);
  useEffect(() => {
    const is_guest = session?.user.is_guest || "";
    setIsGuest(is_guest === "1");
  }, [session]);

  const { checkoutOtp, setActiveStep } = useCheckoutStore();

  const updateMutation = useMutation({
    mutationFn: (data: object) =>
      postRequest({
        path: { name: "account" },
        body: { ...data },
        method: "PUT",
        lang,
      }),
  });

  const handleRegister = async () => {
    setIsLoading(true);
    const savedFormData = localStorage.getItem("register-formData");

    if (!savedFormData) {
      setMessage({
        text: t(i18n)`An unexpected error occurred, please try again.`,
        severity: "warning",
      });
      setIsLoading(false);
      return;
    }

    const formData = JSON.parse(savedFormData);
    const {
      email,
      password,
      firstname,
      lastname,
      phone: telephone,
      countryCode,
      checkboxState,
      customer_group_id,
    } = formData;
    const offerz_plus_terms = formData.checkboxState.isTermsChecked;

    try {
      if (!isGuest) {
        const result = await signIn("credentials", {
          redirect: false,
          email,
          password,
          firstname,
          lastname,
          telephone,
          otp,
          offerz_plus_terms,
          customer_group_id,
          action: "register",
          newsletter: checkboxState.isNewsletterChecked ? 1 : 0,
          agree: checkboxState.isTermsChecked ? 1 : 0,
          lang,
        });
        if (result?.error) {
          setMessage({ text: result.error, severity: "warning" });
        } else {
          if (checkoutOtp) {
            queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
            setActiveStep(2);
          } else {
            router.push("/register/success");
          }
        }
      } else {
        updateMutation.mutate(
          {
            email,
            password,
            telephone: `+${countryCode} ${telephone}`,
            customer_group_id: 1, //register business account group ID - 10 for test, 11 for live
            agree: checkboxState.isPrivacyChecked,
            newsletter: checkboxState.isPrivacyChecked,
            offerz_plus_terms: checkboxState.isTermsChecked,
            email_otp: otp,
            is_guest: 0,
            force_otp: 1,
          },
          {
            onSuccess: async (data: any) => {
              if (data.success === 0) {
                if (data.error.otp) {
                  setMessage({
                    text: data.error.otp,
                    severity: "warning",
                  });
                } else {
                  setMessage({
                    text: t(i18n)`An unexpected error occurred. Please try again later.`,
                    severity: "error",
                  });
                }
                return;
              }
              localStorage.removeItem("register-formData");
              await update({
                first_name: data.data.firstname,
                last_name: data.data.lastname,
                email: data.data.email,
                is_guest: "",
              });
            },
            onError: (error: any) => {
              console.error(error);
            },
          },
        );
      }
    } catch (error) {
      setMessage({
        text: t(i18n)`An error occurred during registration: ${error}`,
        severity: "warning",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOtp = async () => {
    setIsResendOtpLoaderVisible(true);
    const savedFormData = localStorage.getItem("register-formData");

    if (!savedFormData) {
      console.error("No form data found in localStorage.");
      setIsResendOtpLoaderVisible(false);
      return;
    }

    const parsedFormData = JSON.parse(savedFormData);
    const formData = new FormData();
    formData.append("email", parsedFormData.email);
    if (formData) {
      resendOtpMutation.mutate(formData);
    }
  };
  useEffect(() => {
    const savedFormData = localStorage.getItem("register-formData");
    if (savedFormData) {
      const parsedFormData = JSON.parse(savedFormData);
      setEmail(parsedFormData.email);
    }
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress <= 0) {
          clearInterval(timer);
          setIsProgressDisplayed(false);
          return 0;
        }
        return prevProgress - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const savedEmail = localStorage.getItem("current-email");
    setResendOTP(email !== savedEmail);
    setIsProgressDisplayed(email !== savedEmail);
  }, [email]);
  return (
    <div className="mx-auto w-full max-w-[450px] px-[15px] md:px-0">
      <Typography variant="h4" sx={{ mb: "15px" }}>
        <Trans>Verify email</Trans>
      </Typography>
      <Typography variant="body2" sx={{ mb: "40px" }}>
        <Trans>
          We have sent you a 4-digit code via email. Please enter the code in the field below to
          verify your email.
        </Trans>
      </Typography>

      <Box sx={{ mb: "20px" }}>
        <TextField
          placeholder={t(i18n)`Code`}
          required
          value={otp}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleRegister();
            }
          }}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            input.value = input.value.replace(/\D/g, "");
          }}
          inputProps={{ maxLength: 4 }}
          onChange={(e) => setOtp(e.target.value)}
          InputProps={{
            endAdornment: resendOTP && (
              <InputAdornment position="end" sx={{ pr: 0 }}>
                {isResendOtpLoaderVisible ? (
                  <CircularProgress size={20} sx={{ color: "primary.main" }} />
                ) : (
                  <Button
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      "&.Mui-disabled": { color: "primary.main", opacity: 0.6 },
                    }}
                    disabled={isProgressDisplayed}
                    variant="text"
                    disableRipple
                    onClick={handleResendOtp}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Trans>Resend</Trans>
                  </Button>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ mb: "20px" }}>
        <TextField
          value={email ?? ""}
          disabled
          sx={{
            "& .MuiOutlinedInput-root .Mui-disabled": {
              WebkitTextFillColor: "#000",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ pr: 0 }}>
                <Button
                  variant="text"
                  sx={{ fontWeight: 400, fontSize: "14px" }}
                  disableRipple
                  onClick={handleChange}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Trans>Change</Trans>
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isProgressDisplayed && (
        <Box sx={{ mb: "40px" }}>
          <Typography variant="h6" align="center" sx={{ mb: "10px" }}>
            <Trans>
              You may resend the<b className="px-[3px]">OTP</b>in
              <span className="px-[3px]">{progress}</span>
              seconds
            </Trans>
          </Typography>
          <LinearProgress variant="determinate" value={(20 - progress) * 5} sx={{ mb: "20px" }} />
        </Box>
      )}

      <Button
        onClick={handleRegister}
        color="primary"
        variant="contained"
        fullWidth
        sx={{ mb: "50px", overflow: "hidden" }}
      >
        <Trans>Continue</Trans>
        <Typography component="span" className={`${isLoading ? "loading" : ""}`}></Typography>
      </Button>
    </div>
  );
};

export default OtpForm;
